import { convertDistance, getDistance } from "geolib";
import { LocationCoordinates } from "../services/location-tracking";
/**
 * Convert degrees to radians
 */
function toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
function toDeg(radians: number): number {
    return radians * (180 / Math.PI);
}

/**
 * Calculate bearing between two coordinates in degrees (0-360)
 * 0° = North, 90° = East, 180° = South, 270° = West
 */
export function calculateBearing(
    from: LocationCoordinates,
    to: LocationCoordinates
): number {
    const dLon = toRad(to.longitude - from.longitude);
    const lat1 = toRad(from.latitude);
    const lat2 = toRad(to.latitude);

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x =
        Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

    const bearing = Math.atan2(y, x);
    return (toDeg(bearing) + 360) % 360;
}

/**
 * Linear interpolation between two coordinates
 * @param from Starting coordinate
 * @param to Ending coordinate
 * @param progress Progress from 0 to 1
 * @returns Interpolated coordinate
 */
export function interpolateCoordinates(
    from: LocationCoordinates,
    to: LocationCoordinates,
    progress: number
): LocationCoordinates {
    // Clamp progress between 0 and 1
    const t = Math.max(0, Math.min(1, progress));

    return {
        latitude: from.latitude + (to.latitude - from.latitude) * t,
        longitude: from.longitude + (to.longitude - from.longitude) * t,
        speed: to.speed,
        heading: to.heading,
        accuracy: to.accuracy,
        altitude: to.altitude,
    };
}

/**
 * Calculate ETA in minutes based on distance and speed
 * @param distance Distance in kilometers
 * @param currentSpeed Current speed in km/h (optional)
 * @param averageSpeed Average speed to use if current speed is not available or too low (default: 40 km/h)
 * @returns ETA in minutes
 */
export function calculateETA(
    distance: number,
    currentSpeed?: number | null,
    averageSpeed: number = 40
): number {
    // If distance is 0 or negative, ETA is 0
    if (distance <= 0) return 0;

    // Use current speed if available and reasonable (> 5 km/h)
    // For very low speeds, we use a minimum "crawling" speed of 10 km/h 
    // to avoid skyrocketing ETAs, unless they are literally stopped.
    let speed = averageSpeed;

    if (currentSpeed !== null && currentSpeed !== undefined) {
        if (currentSpeed > 5) {
            speed = currentSpeed;
        } else if (currentSpeed > 0) {
            speed = 10; // Minimum crawling speed for calculation
        } else {
            speed = averageSpeed; // Fallback to average if stopped
        }
    }

    const eta = (distance / speed) * 60;
    return isNaN(eta) || !isFinite(eta) ? 0 : eta;
}

/**
 * Format ETA in minutes to human-readable string
 * @param totalMinutes ETA in minutes
 * @returns Formatted string like "5 min" or "1h 23min"
 */
export function formatETA(totalMinutes: number): string {
    if (totalMinutes <= 0) return 'Arrived';

    const totalSeconds = Math.round(totalMinutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    let timeString = '';

    if (hours > 0) {
        timeString += `${hours} hr${hours > 1 ? 's' : ''} `;
    }

    if (minutes > 0) {
        timeString += `${minutes} min${minutes > 1 ? 's' : ''}`;
    }

    // Display seconds only if the destination is less than a minute away
    if (hours === 0 && minutes === 0) {
        if (seconds < 10 && seconds > 0) return 'Arriving now';
        timeString = `${seconds} sec${seconds > 1 ? 's' : ''}`;
    }

    return timeString.trim();
}

/**
 * Format distance to human-readable string (miles/feet)
 * @param km Distance in kilometers
 * @returns Formatted string like "500 ft" or "2.5 miles"
 */
export function formatDistance(km: number): string {
    return `${convertDistance(km * 1000, 'mi').toFixed(2)} miles`;
}

function finiteNumber(value: unknown): number | null {
    if (value == null || value === "") {
        return null;
    }

    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function finiteCoordinate(
    point: { latitude?: unknown; longitude?: unknown } | null | undefined,
): { latitude: number; longitude: number } | null {
    const latitude = finiteNumber(point?.latitude);
    const longitude = finiteNumber(point?.longitude);
    if (latitude == null || longitude == null) {
        return null;
    }

    return { latitude, longitude };
}

/**
 * Recalculate ETA and distance for a booking based on current location and destination
 * @param currentLocation Current location of driver (including speed in m/s)
 * @param destination Destination coordinate
 * @returns Object containing distance in KM and duration in minutes, or null when a coordinate is missing
 */
export function recalculateBookingETA(
    currentLocation: (LocationCoordinates | { latitude?: unknown; longitude?: unknown; speed?: number | null }) | null | undefined,
    destination: { latitude?: unknown; longitude?: unknown } | null | undefined,
): { distance: number; duration: number } | null {
    const from = finiteCoordinate(currentLocation);
    const to = finiteCoordinate(destination);
    if (!from || !to) {
        return null;
    }

    // geolib.getDistance returns meters, convert to KM. Null coordinates throw inside geolib.
    const distanceMeters = getDistance(from, to);
    const distanceKm = distanceMeters / 1000;

    // speed is in m/s, convert to km/h
    const speedKmh = (currentLocation?.speed || 0) * 3.6;
    const duration = calculateETA(distanceKm, speedKmh);

    return {
        distance: distanceKm,
        duration: duration,
    };
}

export const GEOFENCE_MAX_ETA_MINUTES = 3;
export const GEOFENCE_MAX_DISTANCE_KM = 0.4;

export function isWithinStatusGeofence(
    distanceTravelled: { duration: number; distance: number } | null | undefined,
): boolean {
    if (!distanceTravelled) {
        return false;
    }

    return (
        distanceTravelled.duration <= GEOFENCE_MAX_ETA_MINUTES &&
        distanceTravelled.distance <= GEOFENCE_MAX_DISTANCE_KM
    );
}
